import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, FormGroup, Label, Row } from 'reactstrap';
import { ComboBox } from '../../dropdowns';
import moment from 'moment';
import api from "../../../services/api";
import _ from 'lodash';
import { useFlexLayout, useRowSelect, useTable } from 'react-table';
import Grid from '../../Grid';
import { IndeterminateCheckbox } from '../../react-table/controls';

const columnSizing = {
    sm: 12,
    md: {
        size: 3,
        //offset: 3
    }
}

export default function CandidateSelector(props) {
    const {
        setLoading,
        data,
        apiUrl,
        getDefaultSelectedAllocation,
        onCandidateChanged,
        onSelectedAllocationHistoryChanged,
        disabled,
    } = props;
    
    const [itemInEdit, setItemInEdit] = useState(data ? {...data} : {});
    const [reloadHistory, setReloadHistory] = useState(false);
    const [allocationHistory, setAllocationHistory] = useState([]);
    const [refreshItemInEditForHistory, setRefreshItemInEditForHistory] = useState(false);
    const skipPageResetRef = useRef();

    useEffect(() => {
        if (data?.candidateId !== itemInEdit?.candidateId)
            setReloadHistory(true);

        setItemInEdit(data ? {...data} : {});
    }, [data]);

    const loadAllocationHistory = useCallback(() => {
        if (!itemInEdit?.candidateId)
            return;

        setLoading(true);

        api.get(`${apiUrl}/${itemInEdit.candidateId}`)
            .then((response) => {
                const {data: responseData} = response;
                const {allocationHistory, employmentType, candidateAvailableForHiring, candidateResidencyValidated} = responseData;
                // console.log({responseData, allocationHistory});
                setAllocationHistory(allocationHistory ?? []);

                if (data?.candidateId !== itemInEdit?.candidateId)
                    onCandidateChanged({candidateId: itemInEdit.candidateId, candidate: itemInEdit.candidate, employmentType, candidateAvailableForHiring, candidateResidencyValidated});
            })
            .catch((error) => console.error({error}))
            .finally(() => setLoading(false))
        ;
    }, [data?.candidateId, itemInEdit?.candidateId]);

    useEffect(() => {
        if (!reloadHistory)
            return;
        
        setReloadHistory(false);
        loadAllocationHistory();
    }, [reloadHistory]);

    const defaultColumn = useMemo(
        () => ({
            minWidth: 30,
            width: 150,
            maxWidth: 200,
        }), []);

    const allocationHistoryColumns = useMemo(() => {
        const colArray = [
            {
                Header: "Entity",
                accessor: "company.companyName",
            },
            {
                Header: "Project",
                accessor: "project.projectName",
            },
            {
                Header: "Position",
                accessor: "position.positionName",
            },
            {
                Header: "Start Date",
                accessor: (row) => row.startDate ? moment(row.startDate).format("DD/MM/YYYY") : "",
            },
            {
                Header: "End Date",
                accessor: (row) => row.endDate ? moment(row.endDate).format("DD/MM/YYYY") : "",
            },
            {
                Header: "Is Active",
                accessor: (row) => row.active ? "Yes" : "No",
            },
            {
                Header: "Last Paid Date",
                accessor: (row) => row.lastPaidDate ? moment(row.lastPaidDate).format("DD/MM/YYYY") : "",
            },
        ];

        return colArray;
    }, []);

    const {
        getTableProps: allocationHistoryTableProps,
        getTableBodyProps: allocationHistoryTableBodyProps,
        headerGroups: allocationHistoryHeaderGroups,
        rows: allocationHistoryRows,
        selectedFlatRows: selectedAllocationHistoryRows,
        prepareRow: allocationHistoryPrepareRow,
        toggleAllRowsSelected: toggleAllAllocationHistoryRowsSelected,
    } = useTable(
        {
            columns: allocationHistoryColumns,
            data: allocationHistory || [],
            autoResetPage: !skipPageResetRef.current,
            autoResetExpanded: !skipPageResetRef.current,
            autoResetGroupBy: !skipPageResetRef.current,
            autoResetSelectedRows: !skipPageResetRef.current,
            autoResetSortBy: !skipPageResetRef.current,
            autoResetFilters: !skipPageResetRef.current,
            autoResetRowState: !skipPageResetRef.current,
            defaultColumn,
            getRowId: (row) => row.candidateAllocationId,
            initialState: {
                selectedRowIds: getDefaultSelectedAllocation(allocationHistory),
            },
        },
        useFlexLayout,
        hooks => {
            hooks.visibleColumns.push(columns => [
                {
                    id: 'selection',
                    width: 20,
                    disableSortBy: true,
                    Cell: ({ row }) => {
                        const { original } = row;

                        return (
                            <div>
                            {
                                original != null &&
                                <IndeterminateCheckbox
                                    {...row.getToggleRowSelectedProps()}
                                    id={original.candidateAllocationId}
                                    onChange={(e) => {
                                        const selected = row.isSelected;
                                        // console.log(selected);
                                        toggleAllAllocationHistoryRowsSelected(false);
                                        row.toggleRowSelected(!selected);
                                        setRefreshItemInEditForHistory(true);
                                    }}
                                    disabled={disabled}
                                />
                            }
                            </div>
                        )
                    },
                },
                ...columns,
            ])
        },
        useRowSelect,
    );

    useEffect(() => {
        if (!refreshItemInEditForHistory)
            return;

        setRefreshItemInEditForHistory(false);
        onSelectedAllocationHistoryChanged(selectedAllocationHistoryRows);
    }, [refreshItemInEditForHistory]);

    return (
        <>
            <Row className="m-0 p-0">
                <Col xs={12} className="m-0 p-0">
                    <Row>
                        <Col {...columnSizing}>
                            <FormGroup>
                                <Label>
                                    Employee Name
                                    <span className="text-danger">*</span>
                                </Label>
                                <ComboBox
                                    endpoint="/candidate/search/allocated"
                                    isLookup={false}
                                    idField="candidateId"
                                    valueField="candidateName"
                                    minLength={3}
                                    selectedItem={itemInEdit.candidate}
                                    onChange={(item) => {
                                        const newItemInEdit = {...itemInEdit};

                                        if (item?.candidateId === newItemInEdit?.candidateId)
                                            return;

                                        setItemInEdit({ ...newItemInEdit, candidate: item, candidateId: item?.candidateId ?? null });
                                        setReloadHistory(true);
                                    }}
                                    disabled={disabled}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                {
                    allocationHistory.length ?
                    <Row>
                        <Col lg={12}><h4>Allocation History</h4></Col>
                        <Col xs={12}>
                            <Grid
                                rows={allocationHistoryRows}
                                tableProps={allocationHistoryTableProps()}
                                headerGroups={allocationHistoryHeaderGroups}
                                tableBodyProps={allocationHistoryTableBodyProps()}
                                prepareRow={allocationHistoryPrepareRow}
                                height='10vh'
                            />
                        </Col>
                    </Row>
                    :
                    null
                }
                </Col>
            </Row>
        </>
    );
};